import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { personas, PersonaKey, PanelItem } from '../components/dashboard/DashboardData';
import { Eyebrow } from '../components/ui/Reveal';
const statusStyles: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-gold/20 text-gold-dark',
  closed: 'bg-ink/10 text-ink-500'
};
const statusLabel: Record<string, string> = {
  active: 'On track',
  pending: 'In review',
  closed: 'Closed'
};
export function Dashboard() {
  const [active, setActive] = useState<PersonaKey>('seeker');
  const persona = personas.find(p => p.key === active)!;
  return <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-16" data-id="element-289" data-mp="kvlrs17a57ds">
      <div className="max-w-2xl" data-id="element-290" data-mp="fxaibrkwcyw3">
        <Eyebrow data-id="element-291" data-mp="g183eir1c0im">The platform</Eyebrow>
        <h1 className="mt-6 font-serif text-4xl leading-tight text-ink sm:text-5xl" data-id="element-292" data-mp="1wcc31yqk8pe9">
          Dashboards for every role.
        </h1>
        <p className="mt-4 text-lg leading-relaxed text-ink-600" data-id="element-293" data-mp="1rdr2fy7ec3pa">
          One elegant platform, tailored to how each person works. Switch between views to
          preview the experience.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 rounded-full border border-ink/10 bg-ivory-50 p-1.5 shadow-soft sm:inline-flex" data-id="element-294" data-mp="26qufd9hfhwks">
        {personas.map(p => {
        const Icon = p.icon;
        const isActive = p.key === active;
        return <button key={p.key} onClick={() => setActive(p.key)} className={`relative inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? 'text-ivory-100' : 'text-ink-600 hover:text-ink'}`} data-id="element-295" data-mp="1j08dy11xjajr">
              {isActive && <motion.span layoutId="persona-pill" className="absolute inset-0 rounded-full bg-ink" transition={{
            type: 'spring',
            stiffness: 400,
            damping: 34
          }} data-id="element-296" data-mp="108xkbc4tkjo6" />}
              <span className="relative z-10 flex items-center gap-2" data-id="element-297" data-mp="27jlxob5m0o3r">
                <Icon className="h-4 w-4" data-id="element-298" data-mp="22mveheyidce4" />
                {p.label}
              </span>
            </button>;
      })}
      </div>

      <AnimatePresence mode="wait" data-id="element-299" data-mp="2hd2pxaq3lv5b">
        <motion.div key={active} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-10" data-id="element-300" data-mp="2cbddbqn899f4">
          <div className="flex items-center gap-4 rounded-2xl border border-ink/10 bg-ink p-6 text-ivory-100" data-id="element-301" data-mp="1jpkiq4c3513f">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold text-ink" data-id="element-302" data-mp="1tlf8djeq7wfp">
              <persona.icon className="h-5 w-5" data-id="element-303" data-mp="391w472wxwyc9" />
            </span>
            <div data-id="element-304" data-mp="3948vi6ol8l1z">
              <p className="font-serif text-xl" data-id="element-305" data-mp="350kn5ls4tlfo">{persona.name}</p>
              <p className="text-sm text-ink-400" data-id="element-306" data-mp="350123w76qgxf">{persona.role}</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-307" data-mp="3ciikkrx1h62j">
            {persona.metrics.map(m => <div key={m.label} className="rounded-2xl border border-ink/10 bg-ivory-50 p-5 shadow-soft" data-id="element-308" data-mp="3hh3j8mwn7hov">
                <p className="text-xs uppercase tracking-wide text-ink-500" data-id="element-309" data-mp="37m1uhs6a4hmt">{m.label}</p>
                <p className="mt-2 font-serif text-3xl text-ink" data-id="element-310" data-mp="3vab9ob8zm74z">{m.value}</p>
                {m.delta && <p className="mt-1 text-xs font-medium text-emerald-600" data-id="element-311" data-mp="438i3ip8lyu8">{m.delta}</p>}
              </div>)}
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2" data-id="element-312" data-mp="3lixdodg8vpgl">
            {persona.panels.map(panel => <div key={panel.title} className="rounded-2xl border border-ink/10 bg-ivory-50 p-6 shadow-soft" data-id="element-313" data-mp="i41hagqhhi3o">
                <h2 className="font-serif text-lg text-ink" data-id="element-314" data-mp="mwcf9e1nyttl">{panel.title}</h2>
                <div className="mt-5 space-y-3" data-id="element-315" data-mp="2vcvl8r9aafpy">
                  {panel.items.map((item, i) => <PanelRow key={i} item={item} type={panel.type} data-id="element-316" data-mp="2qbpw13eu1al6" data-mpi={i} />)}
                </div>
              </div>)}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>;
}
function PanelRow({
  item,
  type
}: {
  item: PanelItem;
  type: string;
}) {
  if (type === 'progress') {
    return <div data-id="element-317" data-mp="3e4eo8y3b5trz">
        <div className="flex items-center justify-between text-sm" data-id="element-318" data-mp="3e46al3zd2kfq">
          <span className="text-ink-600" data-id="element-319" data-mp="393qz6hyn3n0e">{item.primary}</span>
          <span className="text-ink-500" data-id="element-320" data-mp="34c0no8nyut7e">{item.progress}%</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink/10" data-id="element-321" data-mp="2zdfj5x1olnd5">
          <motion.div className="h-full rounded-full bg-gold" initial={{
          width: 0
        }} animate={{
          width: `${item.progress}%`
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-322" data-mp="2zbcuphg6g2aj" />
        </div>
      </div>;
  }
  return <div className="flex items-center justify-between gap-4 rounded-xl border border-ink/5 bg-ivory-100 px-4 py-3" data-id="element-323" data-mp="4763vjzwyko5">
      <div className="min-w-0" data-id="element-324" data-mp="88rnrpav7z7u">
        <p className="truncate text-sm font-medium text-ink" data-id="element-325" data-mp="d5i144vacgpk">{item.primary}</p>
        {item.secondary && <p className="truncate text-xs text-ink-500" data-id="element-326" data-mp="i5e6a7fpye6p">{item.secondary}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-3" data-id="element-327" data-mp="n50kaj3dfygk">
        {item.status ? <span className={`rounded-full px-2.5 py-1 text-[10px] font-medium ${statusStyles[item.status]}`} data-id="element-328" data-mp="r5ki7ovrr63r">
            {statusLabel[item.status]}
          </span> : <span className="text-xs text-ink-400" data-id="element-329" data-mp="10w5cj04etogv">{item.meta}</span>}
        {item.status && item.meta && <span className="hidden text-xs text-ink-400 sm:inline" data-id="element-330" data-mp="15vi1bqeud5m3">{item.meta}</span>}
      </div>
    </div>;
}